import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getShowcase } from "@/lib/api";
import { scoreColor } from "@/components/Gauge";

const ShowcaseCard = ({ item, i }) => {
  const before = item.score;
  const after = item.projected_score;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
      className="glass rounded-2xl p-5 hover:border-neon-cyan/40 transition-colors duration-300"
      data-testid={`showcase-card-${i}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] tracking-[0.2em] px-2 py-0.5 rounded-md border border-neon-cyan/30 text-neon-cyan">
          {item.platform.toUpperCase()}
        </span>
        <span className="font-mono text-[10px] text-[var(--muted)]">ANONYMIZED</span>
      </div>

      <p className="font-mono text-xs text-[var(--muted)] leading-relaxed mb-4 line-clamp-2 min-h-[2.4rem]">
        “{item.snippet}{item.snippet.length >= 120 ? "…" : ""}”
      </p>

      {/* before -> after bar */}
      <div className="flex items-center justify-between font-mono text-xs mb-1.5">
        <span style={{ color: scoreColor(before) }}>BEFORE {before}</span>
        <ArrowRight size={13} className="text-[var(--muted)]" />
        <span style={{ color: "#22e58a" }}>AFTER {after}</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-[#0b0d14] overflow-hidden border border-[var(--line)]">
        <motion.div
          initial={{ width: `${before}%` }}
          whileInView={{ width: `${after}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background: "linear-gradient(90deg,#ff3b5c 0%,#ffb000 45%,#22e58a 100%)",
            boxShadow: "0 0 14px rgba(34,229,138,0.5)",
          }}
        />
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--line)]">
        <span className="font-mono text-[10px] tracking-[0.15em] text-[var(--muted)]">REBUILD LOOPS</span>
        <span className="font-display font-bold text-sm" style={{ color: "#22e58a", textShadow: "0 0 12px rgba(34,229,138,0.5)" }}>
          ~{item.est_loops_saved} avoided
        </span>
      </div>
    </motion.div>
  );
};

export const Showcase = () => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    getShowcase().then(setItems).catch(() => setItems([]));
  }, []);

  if (!items.length) return null;

  return (
    <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="showcase-section">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-2xl mb-12"
      >
        <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan mb-3">// EXAMPLE AUDIT FEED</p>
        <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-white leading-tight">
          How CoreText reads common build instructions.
        </h2>
        <p className="font-mono text-sm text-[var(--muted)] mt-3">
          Illustrative examples showing how a typical prompt climbs from red to green after an audit.
          These are demonstrations, not customer data.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map((item, i) => (
          <ShowcaseCard key={`${item.created_at}-${item.platform}-${i}`} item={item} i={i} />
        ))}
      </div>
    </section>
  );
};
